import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';

import { BehaviorSubject, Observable, switchMap, tap } from 'rxjs';

import { GetProjectsParams } from '@mirage-projects/cw-commons';
import {
    BrowserView,
    CatalogObject,
    CatalogObjectSelection,
    ObjectBrowserFilter,
    ObjectBrowserSelectionTexts,
    ResultPage,
} from '@mirage-projects/cw-models';

import { PaginationData } from '../../models/pagination-data';
import { UploadWizardService } from '../../services/upload-wizard-service/upload-wizard.service';
import { ObjectBrowserFilterService } from '../object-browser/filter/filter.service';
import { ObjectBrowserComponent } from '../object-browser/object-browser.component';
import { SelectExistingProjectService } from './select-existing-project.service';

/**
 * Shows the list of projects so the user selects one
 * It has pagination and filtering of the projects.
 */
@Component({
    selector: 'cw-select-existing-project',
    templateUrl: 'select-existing-project.component.html',
    styleUrls: ['./select-existing-project.component.scss'],
    standalone: false,
})
export class SelectExistingProjectComponent implements OnInit {
    readonly pageSize = 12;

    /** Enables if the checkbox is selected on project click, in addition to the projectClick sent event */
    @Input() shouldSelectCheckboxOnClick = false;
    /** JobSite id to filter the projects */
    @Input() jobSiteId!: number | undefined;
    /** Emits when the user clicks on the close icon */
    @Output() closeClick = new EventEmitter<void>();

    /** Emits when the user clicks on a project card */
    @Output() projectClick: EventEmitter<CatalogObject> = new EventEmitter<CatalogObject>();

    @ViewChild(ObjectBrowserComponent) objectBrowser!: ObjectBrowserComponent;

    selectionTexts: ObjectBrowserSelectionTexts = {
        selectedElementsText: 'PROJECTS.SHOW_SELECTED',
        showSelectedText: 'PROJECTS.SELECTED_PROJECT',
        showSelectedsText: 'PROJECTS.SELECTED_PROJECTS',
    };

    loading = false;
    browserViews: BrowserView[] = [];
    private readonly data$ = new BehaviorSubject<void>(undefined);
    currentData$ = this.data$.pipe(switchMap(() => this.loadProjects()));

    private currentPage = 1;
    private readonly defaultParams: Partial<GetProjectsParams> = {
        page: this.currentPage,
        page_size: this.pageSize,
    };
    private params: Partial<GetProjectsParams> = this.defaultParams;

    pagination!: PaginationData;

    constructor(
        public uploadWizardService: UploadWizardService,
        public selectExistingProjectService: SelectExistingProjectService,
        private filterService: ObjectBrowserFilterService,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        const filters = this.selectExistingProjectService.initializeFilters();

        this.initializeDefaultFilterParams(filters);

        this.browserViews = [{ id: 'grid', icon: 'service-menu', tooltip: 'OBJECT_BROWSER.GRID_VIEW', filters }];
    }

    /**
     * Emits when the user clicks on the close button
     */
    onClose(): void {
        this.closeClick.emit();
    }

    /**
     * Emits when the filter changes
     * @param filter Filters to apply to theproject list
     */
    filterChanged(filter: Map<string, string>): void {
        const filterData: Partial<GetProjectsParams> = Object.fromEntries(filter.entries());
        this.params = { ...this.defaultParams, ...filterData, page: 1 };

        this.data$.next();
    }

    /**
     * Emits when the user clicks on the page number
     * @param pageNumber Page number to navigate to
     */
    pageChanged(pageNumber: number): void {
        if (this.currentPage !== pageNumber && pageNumber > 0) {
            this.currentPage = pageNumber;
            this.params.page = this.currentPage;

            this.data$.next();
        }
    }

    /**
     * Emits when the user clicks on the refresh button
     */
    dataRefreshed(): void {
        this.data$.next();
    }

    /**
     * For the projects it for the moment has to show the selection checkbox
     */
    hasToShowCheckbox(): boolean {
        return true;
    }

    /**
     * Load the projects for the list
     */
    private loadProjects(): Observable<ResultPage<CatalogObjectSelection>> {
        this.loading = true;
        this.cdr.detectChanges();

        if (this.jobSiteId) {
            this.params.exclude_job_site = this.jobSiteId;
        }

        return this.selectExistingProjectService.loadProjects(this.params).pipe(
            tap((result) => {
                this.pagination = { currentPage: this.currentPage, pageSize: this.pageSize, totalElements: result.count };
                this.loading = false;
            })
        );
    }

    /**
     * Sets default filters params for querying projects
     */
    private initializeDefaultFilterParams(filters: ObjectBrowserFilter[]): void {
        const filtersMap = this.filterService.getFilterParams(filters);
        const filterData: Partial<GetProjectsParams> = Object.fromEntries(filtersMap.entries());

        this.params = { ...this.defaultParams, ...filterData, page: 1 };
    }
}
